import { Box, Typography, useTheme } from "@mui/material";
import * as countries from "country-list";
import { FC } from "react";
import { MinSessionDurationFilter } from "../../../../components";
import type { Dimension } from "../../../../domain/models/metrics";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import { EARLIEST_DATA_DATE } from "../constants";
import { useExperienceOptions } from "../hooks/useExperienceOptions";
import { FilterSelect } from "./FilterSelect";
import { TimeFilter } from "./TimeFilter";

// Prepare country options
const countryOptions = countries
  .getData()
  .map((country) => ({
    label: country.name,
    value: country.code,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

interface FilterToolbarProps {
  selectedCountries: string[];
  selectedExperiences: string[];
  onCountriesChange: (countries: string[]) => void;
  onExperiencesChange: (experiences: string[]) => void;
  onDimensionChange: (dimension: Dimension) => void;
  minSessionDuration: number | null;
  onMinSessionDurationChange: (minutes: number | null) => void;
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  dimension: Dimension;
}

export const FilterToolbar: FC<FilterToolbarProps> = ({
  selectedCountries,
  selectedExperiences,
  onCountriesChange,
  onExperiencesChange,
  onDimensionChange,
  minSessionDuration,
  onMinSessionDurationChange,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  dimension,
}) => {
  const theme = useTheme();
  const { t } = useLocalization();
  const isMobile = useIsMobile();
  const { experienceOptions } = useExperienceOptions();

  return (
    <Box
      sx={{
        alignItems: { xs: "stretch", md: "center" },
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
        justifyContent: "space-between",
        height: { xs: "auto", md: 60 },
      }}
    >
      <TimeFilter
        dimension={dimension}
        earliestSelectableDate={EARLIEST_DATA_DATE}
        endDate={endDate}
        onDimensionChange={onDimensionChange}
        setEndDate={setEndDate}
        setStartDate={setStartDate}
        startDate={startDate}
      />

      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 1,
        }}
      >
        {isMobile && (
          <Typography
            sx={{
              alignSelf: "center",
              fontWeight: 500,
              color: theme.colors.text[1],
              fontSize: 12,
            }}
          >
            {t("pages.dataAnalytics.internalUsage.filterToolbar.filter")}
          </Typography>
        )}
        <MinSessionDurationFilter
          onChange={onMinSessionDurationChange}
          value={minSessionDuration}
        />
        <FilterSelect
          label={t("pages.dataAnalytics.internalUsage.filterToolbar.country")}
          onChange={onCountriesChange}
          options={countryOptions}
          selectedValues={selectedCountries}
        />
        <FilterSelect
          label={t(
            "pages.dataAnalytics.internalUsage.filterToolbar.experience",
          )}
          onChange={onExperiencesChange}
          options={experienceOptions}
          selectedValues={selectedExperiences}
        />
      </Box>
    </Box>
  );
};
